import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import type { RecipeListItem } from "../lib/types";

const METHODS = ["Everything", "Cold maceration", "Warm infusion", "Fat / oleo saccharum", "Blend & sweeten only", "Fermentation"];
const TIMES = ["A weekend", "Two weeks", "A season", "Longer than that"];
const BASES = ["Vodka", "Grappa", "Everclear", "Rye", "Cachaça", "Brandy", "Gin"];
const SORTS = ["Most forked", "Newest", "Quickest"];
const PAGE_SIZE = 20;

export function RecipeIndex() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [recipes, setRecipes] = useState<RecipeListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const q = params.get("q") ?? "";
  const method = params.get("method") ?? "Everything";
  const time = params.get("time") ?? "";
  const base = params.get("base") ?? "";
  const sort = params.get("sort") ?? "Most forked";
  const offset = Number(params.get("offset")) || 0;

  async function load(nextOffset: number) {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (q) qs.set("q", q);
      if (method !== "Everything") qs.set("method", method);
      if (time) qs.set("time", time);
      if (base) qs.set("base", base);
      qs.set("sort", sort);
      qs.set("limit", String(PAGE_SIZE));
      qs.set("offset", String(nextOffset));

      const res = await api.get<{ recipes: RecipeListItem[]; total: number }>(`/recipes?${qs}`);
      setRecipes((prev) => (nextOffset === 0 ? res.recipes : [...prev, ...res.recipes]));
      setTotal(res.total);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, method, time, base, sort]);

  function setParam(key: string, value: string) {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    next.delete("offset");
    setParams(next);
  }

  return (
    <div style={{ display: "grid", gridTemplateColumns: "230px minmax(0,1fr)", alignItems: "start" }}>
      <aside className="filter-sidebar">
        <div className="filter-group">
          <h6 className="eyebrow">Method</h6>
          <div className="flex-col" style={{ gap: 10 }}>
            {METHODS.map((m) => (
              <label key={m} className="radio">
                <input type="radio" name="method" checked={method === m} onChange={() => setParam("method", m === "Everything" ? "" : m)} />
                <span className="dot" />
                {m}
              </label>
            ))}
          </div>
        </div>

        <div className="filter-group">
          <h6 className="eyebrow">Time on your hands</h6>
          <div className="flex-col" style={{ gap: 10 }}>
            {TIMES.map((t) => (
              <label key={t} className="radio">
                <input type="radio" name="time" checked={time === t} onChange={() => setParam("time", time === t ? "" : t)} />
                <span className="dot" />
                {t}
              </label>
            ))}
          </div>
        </div>

        <div className="filter-group">
          <h6 className="eyebrow">Base spirit</h6>
          <div className="wrap-gap">
            {BASES.map((b) => (
              <button
                key={b}
                type="button"
                className={`tag ${base === b ? "tag-accent" : "tag-neutral"}`}
                style={{ border: 0, cursor: "pointer" }}
                onClick={() => setParam("base", base === b ? "" : b)}
              >
                {b}
              </button>
            ))}
          </div>
        </div>

        <hr className="hr" style={{ margin: "30px 0 16px" }} />
        <p className="muted-strong" style={{ margin: 0, fontSize: 12, lineHeight: 1.55 }}>
          Every recipe here carries a real yield, a computed strength and a dated process.
        </p>
      </aside>

      <main className="page" style={{ padding: "36px 40px 90px" }}>
        <div className="page-header" style={{ gap: 32 }}>
          <div>
            <h6 className="kicker">
              {total} recipe{total === 1 ? "" : "s"}
              {q ? ` · matching "${q}"` : ""}
            </h6>
            <h1 className="page-title" style={{ fontSize: 50 }}>
              Bitters, amari
              <br />
              and infusions
            </h1>
          </div>
          <div className="flex-row" style={{ paddingBottom: 6 }}>
            <span className="muted-strong" style={{ fontSize: 11, letterSpacing: "0.08em", textTransform: "uppercase" }}>
              Sort
            </span>
            <div className="seg">
              {SORTS.map((s) => (
                <label key={s} className="seg-opt">
                  <input type="radio" name="sort" checked={sort === s} onChange={() => setParam("sort", s)} />
                  {s}
                </label>
              ))}
            </div>
            <button className="btn btn-secondary" onClick={() => navigate("/recipes/new")}>
              + New recipe
            </button>
          </div>
        </div>

        <hr className="hr" style={{ margin: "28px 0 0" }} />

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "48px minmax(0,3fr) 1.2fr 92px 80px 78px",
            gap: 20,
            padding: "11px 0",
            borderBottom: "1px solid var(--color-divider)",
            fontSize: 11,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
          }}
          className="muted-strong"
        >
          <span>No.</span>
          <span>Recipe</span>
          <span>Base</span>
          <span>Days</span>
          <span>ABV</span>
          <span>Forks</span>
        </div>

        {recipes.map((r) => (
          <div
            key={r.id}
            onClick={() => navigate(`/recipes/${r.id}`)}
            style={{
              display: "grid",
              gridTemplateColumns: "48px minmax(0,3fr) 1.2fr 92px 80px 78px",
              gap: 20,
              padding: "19px 0",
              borderBottom: "1px solid var(--color-divider)",
              alignItems: "baseline",
              cursor: "pointer",
            }}
          >
            <span className="tabular" style={{ fontFamily: "var(--font-heading)", fontWeight: 800, fontSize: 13 }}>
              {r.num}
            </span>
            <span style={{ minWidth: 0 }}>
              <span style={{ display: "block", fontFamily: "var(--font-heading)", fontWeight: 800, fontSize: 21, letterSpacing: "-0.015em" }}>
                {r.title}
              </span>
              <span className="muted" style={{ display: "block", marginTop: 6, fontSize: 13, lineHeight: 1.5 }}>
                {r.blurb}
              </span>
            </span>
            <span style={{ fontSize: 13.5, opacity: 0.78 }}>{r.base}</span>
            <span className="tabular" style={{ fontSize: 14 }}>
              {r.days}
            </span>
            <span className="tabular" style={{ fontFamily: "var(--font-heading)", fontWeight: 800, fontSize: 15, color: "var(--color-accent-700)" }}>
              {r.abv}
            </span>
            <span className="muted-strong tabular" style={{ fontSize: 14 }}>
              {r.forks}
            </span>
          </div>
        ))}

        {recipes.length === 0 && !loading && (
          <p className="muted" style={{ padding: "30px 0" }}>
            No recipes match those filters yet.
          </p>
        )}

        {recipes.length < total && (
          <div className="flex-row" style={{ marginTop: 30 }}>
            <button className="btn btn-secondary" onClick={() => load(offset + PAGE_SIZE)} disabled={loading}>
              Load {Math.min(PAGE_SIZE, total - recipes.length)} more
            </button>
            <span className="muted-strong" style={{ fontSize: 12.5 }}>
              Showing {recipes.length} of {total}
            </span>
          </div>
        )}
      </main>
    </div>
  );
}
